import { useLazyQuery } from '@apollo/client'
import { Button, Typography, useMediaQuery, useTheme } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import Pagination from '@material-ui/lab/Pagination'
import { matchSorter } from 'match-sorter'
import React, { useEffect, useMemo, useState } from 'react'

import PageHeader from '../../components/PageHeader'
import { CONTRACTS_QUERY } from '../../graphql/contracts'
import {
  getEntityCode,
  getEntityId,
  getRoleFromStorage
} from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import contractPagesRoleManagement from '../../staticRoleManagement/contractRoleManagement'
import { ROLE } from '../../staticRoleManagement/types'
import { Contract } from '../../types/contract'
import ContractFilters from './ContractsFilters'
import ContractsTable from './ContractsTable'
import useStyles from './styles/index.styles'

const ContractsList: React.FC = () => {
  const theme = useTheme()
  const classes = useStyles()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [autoCompleteForProviderCode, setAutoCompleteForProviderCode] =
    useState<any>(null)
  const [template, setTemplate] = useState<any>(null)

  const [searchInputValue, setsearchInputValue] = useState<string>('')
  const [perPage] = useState(5)
  const [page, setPage] = useState(1)
  const [count, setcount] = useState<number>(0)
  const [filteredContracts, setfilteredContracts] = useState<Contract[]>([])
  const [getContracts, { data, loading, refetch }] = useLazyQuery<{
    contracts: Contract[]
  }>(CONTRACTS_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const isRoleProvider = getRoleFromStorage() === ROLE.PROVIDER

  useEffect(() => {
    if (autoCompleteForProviderCode && template) {
      getContracts({
        variables: {
          providerCode: autoCompleteForProviderCode.value,
          templateID: template && template.value
        }
      })
    }
    if (isRoleProvider && template) {
      getContracts({
        variables: {
          providerCode: getEntityCode(),
          templateID: template && template.value
        }
      })
    }
  }, [autoCompleteForProviderCode, template])

  useEffect(() => {
    setPage(1)
    const fullRatePlansList = data?.contracts ? data?.contracts : []
    const newFilteredContracts = matchSorter(
      fullRatePlansList,
      searchInputValue,
      {
        keys: ['Title', 'Channels.0.Code']
      }
    )
    setfilteredContracts(newFilteredContracts)
    setcount(newFilteredContracts.length)
  }, [searchInputValue, loading, template, data])

  const numberOfPages = useMemo(() => Math.ceil(count / perPage), [count])

  const hasAccessToChangeProvider = contractPagesRoleManagement
    .find((item) => item.route === '/contracts/(list|templates)')
    ?.sections?.PROVIDER_SELECTOR?.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  return (
    <div>
      <PageHeader title={getTranslate('Contracts')} />

      <div className={classes.header}>
        <Typography variant="h3" gutterBottom>
          {getTranslate('Contracts List')}
        </Typography>
        {contractPagesRoleManagement
          .find((item) => item.route === '/contracts/(list|templates)')
          ?.sections?.ADD_CONTRACT_BUTTON.rolesListWithWriteAccess?.includes(
            getRoleFromStorage()
          ) && (
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            color="primary"
            component={LocalizedLink}
            to="/contracts/list/create">
            {getTranslate(`Add${isSmallScreen ? '' : ' Contract'}`)}
          </Button>
        )}
      </div>
      <ContractFilters
        hasAccessToChangeProvider={hasAccessToChangeProvider}
        setAutoCompleteForProviderCode={setAutoCompleteForProviderCode}
        inputValue={searchInputValue}
        setinputVal={setsearchInputValue}
        setTemplate={setTemplate}
        template={template}
        autoCompleteForProviderCode={autoCompleteForProviderCode}
        isRoleProvider={isRoleProvider}
      />
      {((autoCompleteForProviderCode &&
        autoCompleteForProviderCode.value &&
        template) ||
        (isRoleProvider && template)) && (
        <ContractsTable
          reloadData={refetch}
          data={filteredContracts.slice((page - 1) * perPage, page * perPage)}
          loading={loading}
          providerID={
            isRoleProvider
              ? getEntityId()
              : autoCompleteForProviderCode
              ? autoCompleteForProviderCode.id
              : ''
          }
          providerCode={
            isRoleProvider
              ? getEntityCode()
              : autoCompleteForProviderCode
              ? autoCompleteForProviderCode.value
              : ''
          }
          templateID={template ? template.value : ''}
        />
      )}

      {((autoCompleteForProviderCode && template && template) ||
        (isRoleProvider && template)) && (
        <div className={classes.paginationContainer}>
          <Pagination
            count={numberOfPages}
            color="primary"
            size={isSmallScreen ? 'small' : 'medium'}
            page={page}
            onChange={(_, value) => setPage(value)}
          />
        </div>
      )}
    </div>
  )
}

export default ContractsList
